import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ProductCompareTheme } from "../../../constants";
import { WebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IProductTileAttributeRow,
    ITileID,
    isoTileID,
} from "../../../models/product-compare";
import { usePageSetting } from "../../../utils/settings";
import { useRequestPath } from "../../../utils/urls";
import { ProductCompareTileAttributeRow } from "../ProductCompareTileAttributeRow";
import { ProductCompareTileCTA } from "../ProductCompareTileCTA";
import { ProductCompareTileHeader } from "../ProductCompareTileHeader";
import { getSelectedVariant } from "../selectors";
import { setShowStartingAt } from "../utility";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

const removeFromURLParams = (tileID: ITileID): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const history = window.history;
    const urlParams = new URLSearchParams(window.location.search);
    const selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
        .split(",")
        .map(Number)
        .map(isoTileID.wrap);
    const newTileIDs = selectedTileIDs.filter((id) => id !== tileID);

    if (newTileIDs.length > 0) {
        urlParams.set("tiles", newTileIDs.join(","));
        const newURL = `${window.location.origin}${
            window.location.pathname
        }?${urlParams.toString()}`;
        history.replaceState(history.state, "", newURL);
    } else {
        urlParams.delete("tiles");
        const newURL = `${window.location.origin}${
            window.location.pathname
        }?${urlParams.toString()}`;
        history.replaceState(history.state, "", newURL);
    }
};

const addToURLParams = (tileID: ITileID): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const history = window.history;
    const urlParams = new URLSearchParams(window.location.search);
    let selectedTileIDs: ITileID[] = [];
    if (urlParams.get("tiles")) {
        selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
            .split(",")
            .map(Number)
            .map(isoTileID.wrap);
    }
    const newTileIDs = [...selectedTileIDs, tileID];

    urlParams.set("tiles", newTileIDs.join(","));
    const newURL = `${window.location.origin}${
        window.location.pathname
    }?${urlParams.toString()}`;
    history.replaceState(history.state, "", newURL);
};

const isChecked = (
    tile: ICompareTile,
    selectedTileIDs: readonly ITileID[],
): boolean => {
    return selectedTileIDs.includes(tile.product_compare_tile.id);
};

const buildCompareCount = (
    selectedTileIDs: readonly ITileID[],
): string | null => {
    if (selectedTileIDs.length > 0) {
        return `(${selectedTileIDs.length})`;
    } else {
        return null;
    }
};

const buildCompareNowURI = (
    baseURL: WebPageURL,
    selectedTileIDs: readonly ITileID[],
    requestPath: string,
): string => {
    const returnURL = encodeURIComponent(requestPath);
    const ids = encodeURIComponent(selectedTileIDs.join(","));
    return `${baseURL}?tiles=${ids}&return_url=${returnURL}`;
};

const buildAttributeRow = (
    tile: ICompareTile,
    financingLink: WebPageURL,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null,
    highestAttributeCount?: number,
): React.ReactNode => {
    return tile.rows.map((row: IProductTileAttributeRow) => (
        <ProductCompareTileAttributeRow
            key={`${tile.product_compare_tile.id}-${row.row_type.id}`}
            row={row}
            data={tile}
            financingLink={financingLink}
            sizeAttributeOptionGroup={sizeAttributeOptionGroup}
            highestAttributeCount={highestAttributeCount}
        />
    ));
};

export const ProductSelectionGridColumn = (
    props: Immutable<{
        tile: ICompareTile;
        showStartingAt?: boolean;
        showRating?: boolean;
        productComparePageURL: WebPageURL | null;
        selectedTileIDs: readonly ITileID[];
        priceSelectValue: string;
        financingLink: WebPageURL;
        sizeAttributeOptionGroup: IAttributeOptionGroup | null;
        theme: ProductCompareTheme;
        highestAttributeCount?: number;
    }> & {
        addToSelected: (tileID: ITileID) => void;
        removeFromSelected: (tileID: ITileID) => void;
        showModal: () => void;
    },
): React.ReactNode => {
    const onCompareCheckboxChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        tile: ICompareTile,
    ): void => {
        if (e.currentTarget.checked) {
            if (props.selectedTileIDs.length < 4) {
                props.addToSelected(tile.product_compare_tile.id);
                addToURLParams(tile.product_compare_tile.id);
            } else {
                props.showModal();
            }
        } else {
            props.removeFromSelected(tile.product_compare_tile.id);
            removeFromURLParams(tile.product_compare_tile.id);
        }
    };

    const showCompareFunctionality = [
        ProductCompareTheme.WITHOUT_COMPARE,
        ProductCompareTheme.THEME_ONE,
    ].includes(props.theme)
        ? false
        : true;
    const variant = getSelectedVariant(
        props.tile,
        props.sizeAttributeOptionGroup ? props.priceSelectValue : null,
    );
    const tileClasses = classNames({
        ["tile"]: true,
        [styles.tile]: props.theme === ProductCompareTheme.DEFAULT,
        [styles.tileThemeWithoutCompare]:
            props.theme === ProductCompareTheme.WITHOUT_COMPARE,
        [styles.tileThemeOne]: props.theme === ProductCompareTheme.THEME_ONE,
        [props.theme || ""]: true,
        ["hidden"]: props.tile.is_promo_period_active === false,
    });
    const highlightedAttributesClasses = classNames({
        [styles.highlightedAttributes]:
            props.theme === ProductCompareTheme.DEFAULT,
        [styles.highlightedAttributesThemeWithoutCompare]:
            props.theme === ProductCompareTheme.WITHOUT_COMPARE,
        [styles.highlightedAttributesThemeOne]:
            props.theme === ProductCompareTheme.THEME_ONE,
    });
    const requestPath = useRequestPath();
    const appSlug = usePageSetting("app_slug") || "";
    const isCanada = appSlug === AppSlug.TEMPURCA;
    const propShowRating =
        props.showRating !== undefined ? props.showRating : true;
    const showRating = isCanada ? false : propShowRating;

    return (
        <div
            className={tileClasses}
            role="group"
            aria-label={t`Mattress Model`}
        >
            <ProductCompareTileHeader
                data={props.tile}
                selectedVariant={variant}
                showDescription={true}
                showRating={showRating}
                theme={props.theme}
            />
            <section className={highlightedAttributesClasses}>
                {buildAttributeRow(
                    props.tile,
                    props.financingLink,
                    props.sizeAttributeOptionGroup,
                    props.highestAttributeCount,
                )}
            </section>
            <ProductCompareTileCTA
                data={props.tile}
                selectedVariant={variant}
                showStartingAt={
                    props.showStartingAt
                        ? props.showStartingAt
                        : setShowStartingAt(
                              props.tile,
                              props.sizeAttributeOptionGroup,
                          )
                }
                financingLink={props.financingLink}
                theme={props.theme}
                visibleOverride={isCanada}
            />
            {showCompareFunctionality && (
                <form className={styles.form}>
                    <span className="ada-screenreader-only">
                        {t`Activating these elements will cause content on the page to be updated. Please go to the Compare section of the page to view your results.`}
                    </span>
                    <input
                        id={`product-compare-tile-selected-${props.tile.product_compare_tile.id}`}
                        onChange={(e) => {
                            onCompareCheckboxChange(e, props.tile);
                        }}
                        type="checkbox"
                        className="al-comparisongrid-checked"
                        checked={isChecked(props.tile, props.selectedTileIDs)}
                    />
                    <label
                        htmlFor={`product-compare-tile-selected-${props.tile.product_compare_tile.id}`}
                    >
                        {isChecked(props.tile, props.selectedTileIDs)
                            ? `Selected`
                            : `Compare`}
                    </label>
                </form>
            )}
            {isChecked(props.tile, props.selectedTileIDs) &&
                showCompareFunctionality &&
                props.productComparePageURL && (
                    <a
                        href={buildCompareNowURI(
                            props.productComparePageURL,
                            props.selectedTileIDs,
                            requestPath,
                        )}
                        className="al-comparsiongrid-compareselected"
                    >
                        Compare Now{" "}
                        <span>{buildCompareCount(props.selectedTileIDs)}</span>
                    </a>
                )}
        </div>
    );
};
